// O2D.W-PRD-027 §TR1 — read-only selection-state reader.
//
// `readSelectionMarkState(state)` classifies the live TipTap text selection
// into the 3-state model (ON / OFF / MIXED) for each of the eight per-run
// formatting dimensions, so the radial options-bar can highlight the selected
// run's actual formatting instead of the board object's properties.
//
// The reader is PURE and READ-ONLY: it touches only `state.doc`,
// `state.selection`, and `state.storedMarks`. It never calls `setMark`,
// `applyFontSize`, `chain()`, or `.focus()`, so reading the selection can
// never mutate the document or steal focus (Failure Condition FC1 / Goal G4).
//
// Two selection shapes are handled (FC8):
//  - RANGE (`!selection.empty`): walk `doc.nodesBetween(from, to)` and classify
//    on/off/mixed across the runs.
//  - COLLAPSED caret (`selection.empty`): read the caret marks via
//    `storedMarks ?? selection.$from.marks()` and classify on/off only — a
//    single point is never MIXED.

import type { EditorState } from '@tiptap/pm/state';
import type { Mark } from '@tiptap/pm/model';
import type {
    SelectionDimension,
    SelectionMarkState,
    SelectionMarkSnapshot,
} from './selection-mark-state.type.js';

/** The eight dimensions, in stable order, used to build the snapshot. */
const ALL_DIMENSIONS: readonly SelectionDimension[] = [
    'italic',
    'underline',
    'strike',
    'color',
    'fontSize',
    'fontWeight',
    'textTransform',
    'verticalScript',
];

/** Valued dimensions carry a `textStyle` attribute value when ON; the three
 * boolean marks (italic/underline/strike) carry presence only. */
const VALUED_DIMENSIONS = new Set<SelectionDimension>([
    'color',
    'fontSize',
    'fontWeight',
    'textTransform',
    'verticalScript',
]);

/**
 * Observe one dimension on a single run's marks.
 * Returns the value string when present, or `null` when absent/off.
 */
function valueForDimension(marks: readonly Mark[], dim: SelectionDimension): string | null {
    if (!VALUED_DIMENSIONS.has(dim)) {
        return marks.some((m) => m.type.name === dim) ? 'on' : null;
    }
    const textStyle = marks.find((m) => m.type.name === 'textStyle');
    const raw = textStyle?.attrs?.[dim];
    return raw === null || raw === undefined || raw === '' ? null : String(raw);
}

/** Build the ON state, attaching the uniform value only for valued dimensions. */
function onState(dim: SelectionDimension, value: string): SelectionMarkState {
    return VALUED_DIMENSIONS.has(dim) ? { state: 'on', value } : { state: 'on' };
}

/**
 * Classify a dimension across a range from its per-run observations:
 * all absent → off; a single shared value → on; disagreement (≥2 distinct
 * values, including an on-vs-off split) → mixed.
 */
function classifyRange(
    dim: SelectionDimension,
    values: ReadonlyArray<string | null>,
): SelectionMarkState {
    const present = [...new Set(values.filter((v): v is string => v !== null))];
    const hasOff = values.some((v) => v === null);
    if (present.length === 0) {
        return { state: 'off' };
    }
    if (present.length === 1 && !hasOff) {
        return onState(dim, present[0]!);
    }
    // Valued dimensions carry their distinct present values so the options-bar
    // can dash exactly the present slots (O2D.W-PRD-031 §TR1); boolean marks
    // stay a bare tri-state MIXED.
    return VALUED_DIMENSIONS.has(dim) ? { state: 'mixed', present } : { state: 'mixed' };
}

/** Classify a dimension at a collapsed caret — on/off only, never mixed. */
function classifyCaret(dim: SelectionDimension, value: string | null): SelectionMarkState {
    return value === null ? { state: 'off' } : onState(dim, value);
}

/** Empty per-dimension observation buckets for the range walk. */
function emptyBuckets(): Record<SelectionDimension, (string | null)[]> {
    return ALL_DIMENSIONS.reduce(
        (acc, dim) => {
            acc[dim] = [];
            return acc;
        },
        {} as Record<SelectionDimension, (string | null)[]>,
    );
}

/** Read a non-empty range: collect per-dimension run values, then classify. */
function readRange(state: EditorState): SelectionMarkSnapshot {
    const { from, to } = state.selection;
    const buckets = emptyBuckets();
    state.doc.nodesBetween(from, to, (node) => {
        if (!node.isText) {
            return;
        }
        for (const dim of ALL_DIMENSIONS) {
            buckets[dim].push(valueForDimension(node.marks, dim));
        }
    });
    const snapshot = {} as Record<SelectionDimension, SelectionMarkState>;
    for (const dim of ALL_DIMENSIONS) {
        snapshot[dim] = classifyRange(dim, buckets[dim]);
    }
    return snapshot;
}

/** Read a collapsed caret: classify the caret marks on/off only. */
function readCaret(state: EditorState): SelectionMarkSnapshot {
    const marks = state.storedMarks ?? state.selection.$from.marks();
    const snapshot = {} as Record<SelectionDimension, SelectionMarkState>;
    for (const dim of ALL_DIMENSIONS) {
        snapshot[dim] = classifyCaret(dim, valueForDimension(marks, dim));
    }
    return snapshot;
}

/**
 * Read the current selection's formatting state. Branches on whether the
 * selection is collapsed (caret) or a range; both paths are pure reads.
 */
export function readSelectionMarkState(state: EditorState): SelectionMarkSnapshot {
    return state.selection.empty ? readCaret(state) : readRange(state);
}
